import { useEffect } from "react";
import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { loginRequest } from "@/features/auth/authConfig";
import { useUserByEmail } from "@/services/settings";
import { AccessDeniedPage } from "@/features/auth/AccessDeniedPage";

interface ProtectedRouteProps {
  children: ReactNode;
}

const menuPermissionMap = [
  { path: "/dashboard", permission: "CanViewDashboard" },
  { path: "/grcdashboard", permission: "CanViewGRCReport" },
  { path: "/draftcalendar", permission: "CanViewDraftCalender" },
  { path: "/calendarscheduleview", permission: "CanViewDraftCalender" },
  { path: "/settings", permission: "CanViewSettings" },
  { path: "/message-monitor", permission: "CanViewMessageLog" },
] as const;

function hasPermission(userPermissions: string[], permission: string): boolean {
  return userPermissions.includes(permission);
}

function requiredPermissionForPath(pathname: string): string | null {
  if (pathname === "/") return "CanViewDashboard";

  const menu = menuPermissionMap.find((item) =>
    pathname === item.path || pathname.startsWith(`${item.path}/`)
  );

  return menu?.permission ?? null;
}

function firstAllowedPath(userPermissions: string[]): string | null {
  const firstAllowed = menuPermissionMap.find((item) =>
    hasPermission(userPermissions, item.permission)
  );

  return firstAllowed?.path ?? null;
}

/**
 * Mirrors Blazor LandingPage.razor:
 * 1. Ensures MSAL authentication (redirects to login if not signed in).
 * 2. Fetches the signed-in user from the API by email — once, cached for the session.
 * 3. If the user is not found or has no role assigned → shows AccessDeniedPage.
 * 4. Only renders children when the user is fully authorized.
 *
 * Because useUserByEmail uses staleTime: Infinity, the API call is made exactly
 * once per browser session; every useCurrentUser() / usePermissions() call across
 * the app reads from the same TanStack Query cache entry — no repeated fetches.
 */
export function ProtectedRoute({ children }: Readonly<ProtectedRouteProps>) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const location = useLocation();
  const email = accounts[0]?.username ?? "";

  // Fetch user profile. Enabled only after MSAL auth is complete and we have an email.
  const { data: user, isPending: isUserPending, isError } = useUserByEmail(email, {
    enabled: isAuthenticated && inProgress === InteractionStatus.None && Boolean(email),
  });

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      void instance.loginRedirect(loginRequest);
    }
  }, [instance, isAuthenticated, inProgress]);

  // Still doing MSAL redirect / SSO
  if (inProgress !== InteractionStatus.None) {
    return <AuthSpinner message="Signing in…" />;
  }

  if (!isAuthenticated) {
    return <AuthSpinner message="Redirecting to sign-in…" />;
  }

  // Fetching user profile from API
  if (isUserPending) {
    return <AuthSpinner message="Authorizing…" />;
  }

  // User not in system → access denied
  // Mirrors: if (userdata == null) NavigateTo("access_denied")
  if (!isError && (!user)) {
    return <AccessDeniedPage />;
  }

  // Distinguish API/transient failures from authorization failures.
  if (isError) {
    return <AuthSpinner message="Unable to verify access. Please try again." />;
  }

  const userPermissions = user.permissions ?? [];
  const requiredPermission = requiredPermissionForPath(location.pathname);

  if (requiredPermission && !hasPermission(userPermissions, requiredPermission)) {
    const fallbackPath = firstAllowedPath(userPermissions);

    if (!fallbackPath) {
      return <AccessDeniedPage />;
    }

    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

function AuthSpinner({ message }: Readonly<{ message: string }>) {
  return (
    <div className="flex min-h-screen items-center justify-center gap-3 text-slate-600">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span className="text-sm">{message}</span>
    </div>
  );
}

